/**
 * Members Portal Page
 * 
 * Gateway page for both returning and new members.
 * Features a clean two-column layout with clear pathways.
 * Matches the marketing site's aesthetic with forest/golden theme.
 */

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookOpen, UserPlus, LogIn, Sparkles, Heart } from "lucide-react";

export default function MembersPage() {
  // Function to scroll to top when navigation links are clicked
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-white to-forest-50">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <Badge className="mb-4 bg-forest-100 text-forest-700 hover:bg-forest-200">
            <Sparkles className="w-3 h-3 mr-1" />
            Member Portal
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-forest-600 to-golden-600 bg-clip-text text-transparent">
              Welcome to Your
            </span>
            <br />
            <span className="text-gray-900">Reading Journey</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose your path below to begin your adventure with Brave Things Books
          </p>
        </motion.div>

        {/* Two-Column Layout */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Returning Members Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="h-full border-2 border-forest-200 hover:border-forest-400 transition-all duration-300 hover:shadow-xl">
              <CardContent className="p-8 flex flex-col h-full">
                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-forest-500 to-forest-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <LogIn className="w-8 h-8 text-white" />
                </div>

                {/* Heading */}
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Returning Member?
                </h2>

                {/* Description */}
                <p className="text-gray-600 text-lg mb-6 flex-grow">
                  Welcome back! Sign in to continue your reading journey, access your library, and pick up right where you left off.
                </p>

                {/* Features List */}
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-700">
                    <Heart className="w-5 h-5 text-forest-600 mr-3 flex-shrink-0" />
                    Access your personal library
                  </li>
                  <li className="flex items-center text-gray-700">
                    <Heart className="w-5 h-5 text-forest-600 mr-3 flex-shrink-0" />
                    Resume your reading progress
                  </li>
                  <li className="flex items-center text-gray-700">
                    <Heart className="w-5 h-5 text-forest-600 mr-3 flex-shrink-0" />
                    View your bookmarks
                  </li>
                </ul>

                {/* CTA Button */}
                <Link to="/login" className="w-full">
                  <Button 
                    className="w-full bg-forest-600 hover:bg-forest-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                    onClick={scrollToTop}
                  >
                    <LogIn className="w-5 h-5 mr-2" />
                    Sign In
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>

          {/* New Members Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Card className="h-full border-2 border-golden-200 hover:border-golden-400 transition-all duration-300 hover:shadow-xl">
              <CardContent className="p-8 flex flex-col h-full">
                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-golden-500 to-coral-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <UserPlus className="w-8 h-8 text-white" />
                </div>

                {/* Heading */}
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  New Member?
                </h2>

                {/* Description */}
                <p className="text-gray-600 text-lg mb-6 flex-grow">
                  Join our community of readers! Request access to discover interactive books that teach emotional intelligence through adventure.
                </p>

                {/* Features List */}
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-700">
                    <Sparkles className="w-5 h-5 text-golden-600 mr-3 flex-shrink-0" />
                    Interactive storytelling
                  </li>
                  <li className="flex items-center text-gray-700">
                    <Sparkles className="w-5 h-5 text-golden-600 mr-3 flex-shrink-0" />
                    Mindfulness activities
                  </li>
                  <li className="flex items-center text-gray-700">
                    <Sparkles className="w-5 h-5 text-golden-600 mr-3 flex-shrink-0" />
                    Family learning resources
                  </li>
                </ul>

                {/* CTA Button */}
                <a
                  href="https://forms.clickup.com/2370929/f/28bbh-75451/2WWNJ2GGXUMT0C1NUZ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button className="w-full bg-gradient-to-r from-golden-500 to-coral-500 hover:from-golden-600 hover:to-coral-600 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
                    <UserPlus className="w-5 h-5 mr-2" />
                    Request Access
                  </Button>
                </a>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          <p className="text-gray-600 text-lg">
            Questions? <a href="https://forms.clickup.com/2370929/f/28bbh-75451/2WWNJ2GGXUMT0C1NUZ" target="_blank" rel="noopener noreferrer" className="text-forest-600 hover:text-forest-700 font-semibold underline">Contact us</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
